import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  ShoppingCart,
  AlertTriangle,
  Download,
  RefreshCw,
  Euro,
  Wallet,
  BarChart3,
  PieChart,
  Palette,
  PackageX,
  FileText,
  FileSpreadsheet,
  Truck,
  Clock,
  Route,
  CheckCircle2,
  Calendar,
  Calculator,
  Megaphone,
  Move,
  Check
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend
} from "recharts";
import { saveOrder } from "@/components/SortableWrapper";
import { CardReorderControls } from "@/components/CardReorderControls";

const DEFAULT_CARDS_ORDER = ["paiements", "rapport", "alertes", "sources", "ca-detail"];

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];

const KPICard = ({ title, value, icon: Icon, trend, trendValue, color = "primary", suffix = "€" }) => {
  const colorClasses = {
    primary: "bg-indigo-100 text-indigo-600",
    success: "bg-emerald-100 text-emerald-600",
    warning: "bg-amber-100 text-amber-600",
    danger: "bg-red-100 text-red-600",
    info: "bg-blue-100 text-blue-600"
  };

  return (
    <Card className="card-hover" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-800">
              {typeof value === 'number' ? value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : value}
              {suffix && <span className="text-lg ml-1">{suffix}</span>}
            </p>
            {trend && (
              <div className={`flex items-center gap-1 text-sm ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                <span>{trendValue}</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [rapport, setRapport] = useState(null);
  const [sources, setSources] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [compare, setCompare] = useState(null);
  const [paiementsSummary, setPaiementsSummary] = useState(null);
  const [paiementsMois, setPaiementsMois] = useState(new Date().toISOString().slice(0, 7));
  const [editMode, setEditMode] = useState(false);
  const [cardsOrder, setCardsOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dashboard_cards_order") || "null");
      if (Array.isArray(saved) && saved.length > 0) {
        const missing = DEFAULT_CARDS_ORDER.filter(k => !saved.includes(k));
        return [...saved.filter(k => DEFAULT_CARDS_ORDER.includes(k)), ...missing];
      }
    } catch { /* ignore */ }
    return DEFAULT_CARDS_ORDER;
  });

  const handleCardsReorder = (newOrder) => {
    setCardsOrder(newOrder);
    saveOrder("dashboard_cards_order", newOrder);
  };
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDashboard = async (debut = dateDebut, fin = dateFin) => {
    try {
      const params = {};
      if (debut) params.date_debut = debut;
      if (fin) params.date_fin = fin;
      const promises = [
        axios.get(`${API}/dashboard`, { headers, params }),
        axios.get(`${API}/rapport-journalier`, { headers }),
        axios.get(`${API}/stats/sources`, { headers, params }),
        axios.get(`${API}/dashboard/breakdown-remises`, { headers, params }),
        axios.get(`${API}/paiements/summary${paiementsMois ? `?mois=${paiementsMois}` : ""}`, { headers }),
      ];
      // Comparaison période précédente + N-1 uniquement quand les 2 dates sont posées
      if (debut && fin) {
        promises.push(axios.get(`${API}/dashboard/compare`, { headers, params: { date_debut: debut, date_fin: fin } }));
      } else {
        promises.push(Promise.resolve({ data: null }));
      }
      const [statsRes, rapportRes, sourcesRes, breakdownRes, paiementsRes, compareRes] = await Promise.all(promises);
      setStats(statsRes.data);
      setRapport(rapportRes.data);
      setSources(sourcesRes.data);
      setBreakdown(breakdownRes.data);
      setPaiementsSummary(paiementsRes.data);
      setCompare(compareRes.data);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Erreur lors du chargement du dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRecalculerCA = async () => {
    if (!window.confirm("Recalculer le chiffre d'affaires à partir de toutes les livraisons livrées ? Cela reconstruit les ventes.")) return;
    setRecalculating(true);
    try {
      const res = await axios.post(`${API}/admin/recalculer-ventes`, {}, { headers });
      toast.success(`CA recalculé : ${res.data.ventes_creees} ventes — ${res.data.ca_ttc_total} € TTC`);
      fetchDashboard();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du recalcul");
    } finally {
      setRecalculating(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboard();
  };

  const applyDateFilter = () => {
    setRefreshing(true);
    fetchDashboard(dateDebut, dateFin);
  };

  const resetDateFilter = () => {
    setDateDebut("");
    setDateFin("");
    setRefreshing(true);
    fetchDashboard("", "");
  };

  const applyPreset = (preset) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    let d1 = "", d2 = iso(today);
    if (preset === "today") {
      d1 = iso(today);
    } else if (preset === "week") {
      // ISO : lundi = 1 ; JS getDay(): 0=dim,1=lun,...
      const dow = today.getDay() === 0 ? 7 : today.getDay();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (dow - 1));
      d1 = iso(monday);
    } else if (preset === "mois") {
      d1 = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
    } else if (preset === "mois_prec") {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      d1 = iso(first);
      d2 = iso(last);
    } else if (preset === "30j") {
      const dt = new Date(today); dt.setDate(dt.getDate() - 30);
      d1 = iso(dt);
    } else if (preset === "90j") {
      const dt = new Date(today); dt.setDate(dt.getDate() - 90);
      d1 = iso(dt);
    } else if (preset === "annee") {
      d1 = `${today.getFullYear()}-01-01`;
    }
    setDateDebut(d1);
    setDateFin(d2);
    setRefreshing(true);
    fetchDashboard(d1, d2);
  };

  const handleSeedData = async () => {
    try {
      await axios.post(`${API}/seed-data`, {}, { headers });
      toast.success("Données initialisées avec succès !");
      fetchDashboard();
    } catch (error) {
      toast.error("Erreur lors de l'initialisation");
    }
  };

  const handleExport = async (type, format = 'csv') => {
    try {
      const endpoint = format === 'pdf' ? `${API}/export/${type}/pdf` : `${API}/export/${type}`;
      const qs = [];
      if (type === "dashboard" && dateDebut) qs.push(`date_debut=${dateDebut}`);
      if (type === "dashboard" && dateFin) qs.push(`date_fin=${dateFin}`);
      const url = qs.length ? `${endpoint}?${qs.join("&")}` : endpoint;
      const response = await axios.get(url, {
        headers,
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const suffix = (type === "dashboard" && (dateDebut || dateFin))
        ? `_${dateDebut || "debut"}_${dateFin || "fin"}`
        : "";
      link.setAttribute('download', `${type}_topcocoon${suffix}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export ${format.toUpperCase()} téléchargé !`);
    } catch (error) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleRapportMensuel = async () => {
    try {
      const now = new Date();
      const mois = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const response = await axios.get(`${API}/rapport-mensuel?mois=${mois}`, {
        headers,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `rapport_${mois}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Rapport mensuel téléchargé !");
    } catch {
      toast.error("Erreur lors de la génération du rapport");
    }
  };

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paiementsMois]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const pieData = stats?.ventes_par_canal ? [
    { name: "B2C", value: stats.ventes_par_canal.B2C || 0 },
    { name: "B2B", value: stats.ventes_par_canal.B2B || 0 }
  ] : [];

  // Dynamic model data from API
  const modeleData = stats?.ventes_par_modele || [];
  
  // Category data
  const categorieData = stats?.ventes_par_categorie ? 
    Object.entries(stats.ventes_par_categorie).map(([name, value]) => ({ name, value })) : [];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Dashboard</h1>
          <p className="text-slate-500 mt-1">Vue d'ensemble de votre activité TopCocoon</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            onClick={handleRecalculerCA}
            disabled={recalculating}
            data-testid="recalculer-ca-btn"
          >
            <Calculator className={`w-4 h-4 mr-2 ${recalculating ? 'animate-spin' : ''}`} />
            {recalculating ? "Recalcul..." : "Recalculer le CA"}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="export-dashboard-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('dashboard', 'csv')} data-testid="export-dashboard-csv-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
                Exporter en CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('dashboard', 'pdf')} data-testid="export-dashboard-pdf-btn">
                <FileText className="w-4 h-4 mr-2 text-red-600" />
                Exporter en PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm"
            onClick={handleSeedData}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="seed-data-btn"
          >
            Initialiser données
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditMode(!editMode)}
            className={editMode ? "bg-indigo-600 text-white hover:bg-indigo-700" : ""}
            data-testid="dashboard-edit-toggle"
          >
            {editMode ? <><Check className="w-4 h-4 mr-2" />Terminé</> : <><Move className="w-4 h-4 mr-2" />Réorganiser</>}
          </Button>
          {editMode && (
            <Button variant="outline" size="sm" onClick={() => { localStorage.removeItem("dashboard_cards_order"); setCardsOrder(DEFAULT_CARDS_ORDER); setEditMode(false); }} className="text-red-600" data-testid="dashboard-reset-order">
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="border-slate-200" data-testid="dashboard-date-filter">
        <CardContent className="p-4 flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Date début</label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="dashboard-date-debut-input"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600">Date fin</label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              data-testid="dashboard-date-fin-input"
            />
          </div>
          <Button size="sm" onClick={applyDateFilter} className="bg-indigo-600 hover:bg-indigo-700" data-testid="dashboard-apply-filter-btn">
            Appliquer
          </Button>
          <Button size="sm" variant="outline" onClick={resetDateFilter} data-testid="dashboard-reset-filter-btn">
            Tout
          </Button>
          <div className="flex gap-1 ml-auto flex-wrap">
            <Button size="sm" variant="outline" onClick={() => applyPreset("today")} data-testid="dashboard-preset-today-btn">Aujourd&apos;hui</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("week")} data-testid="dashboard-preset-week-btn">Cette semaine</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("mois")} data-testid="dashboard-preset-mois-btn">Ce mois</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("mois_prec")} data-testid="dashboard-preset-mois-prec-btn">Mois dernier</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("30j")} data-testid="dashboard-preset-30j-btn">30j</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("90j")} data-testid="dashboard-preset-90j-btn">90j</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset("annee")} data-testid="dashboard-preset-annee-btn">Cette année</Button>
          </div>
          {(dateDebut || dateFin) && (
            <span className="text-xs text-indigo-600 font-medium ml-2" data-testid="dashboard-active-filter">
              Filtre actif : {dateDebut || "…"} → {dateFin || "…"}
            </span>
          )}
        </CardContent>
      </Card>

      {/* Comparaison période — visible uniquement quand les 2 dates sont posées */}
      {compare && (
        <Card className="border-indigo-200" data-testid="dashboard-compare-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 font-medium flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-indigo-600" />
              Comparaison de la période
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-100">
              <p className="text-[11px] uppercase text-indigo-600 font-semibold">Période sélectionnée</p>
              <p className="text-2xl font-bold text-indigo-800 mt-1" data-testid="compare-current-ca">
                {(compare.current?.ca_ht ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € HT
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                {compare.current?.start} → {compare.current?.end} ({compare.span_days} j)
              </p>
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-[11px] uppercase text-slate-600 font-semibold">Période précédente</p>
              <p className="text-xl font-semibold text-slate-800 mt-1" data-testid="compare-previous-ca">
                {(compare.previous?.ca_ht ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € HT
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                {compare.previous?.start} → {compare.previous?.end}
              </p>
              {compare.previous?.pct_vs_current !== null && compare.previous?.pct_vs_current !== undefined && (
                <p className={`text-sm font-bold mt-1 ${compare.previous.pct_vs_current >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="compare-previous-pct">
                  {compare.previous.pct_vs_current >= 0 ? "▲" : "▼"} {compare.previous.pct_vs_current}%
                </p>
              )}
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <p className="text-[11px] uppercase text-slate-600 font-semibold">Même période N-1</p>
              <p className="text-xl font-semibold text-slate-800 mt-1" data-testid="compare-y1-ca">
                {(compare.year_ago?.ca_ht ?? 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 })} € HT
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                {compare.year_ago?.start} → {compare.year_ago?.end}
              </p>
              {compare.year_ago?.pct_vs_current !== null && compare.year_ago?.pct_vs_current !== undefined && (
                <p className={`text-sm font-bold mt-1 ${compare.year_ago.pct_vs_current >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="compare-y1-pct">
                  {compare.year_ago.pct_vs_current >= 0 ? "▲" : "▼"} {compare.year_ago.pct_vs_current}%
                </p>
              )}
            </div>
          </CardContent>
          {compare.daily && compare.daily.length > 0 && (
            <CardContent className="pt-0">
              <p className="text-xs font-semibold text-slate-600 mb-2">Évolution jour par jour</p>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={compare.daily} data-testid="compare-daily-chart">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${Number(v).toLocaleString("fr-FR")} € HT`} labelFormatter={(v) => `Jour du ${v}`} />
                  <Line type="monotone" dataKey="ca_ht" name="CA HT" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          )}
        </Card>
      )}

      {/* Zone réorganisable — sections principales (ordre configurable) */}
      <div className="flex flex-col gap-8" data-testid="reorderable-zone">
      {/* Paiements reçus */}
      {paiementsSummary && (
        <div className="relative" style={{ order: cardsOrder.indexOf("paiements") }} data-testid="card-paiements-wrapper">
        <CardReorderControls id="paiements" order={cardsOrder} setOrder={setCardsOrder} editMode={editMode} save={(o) => saveOrder("dashboard_cards_order", o)} />
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 via-indigo-50 to-violet-50" data-testid="paiements-summary-dashboard">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Euro className="w-5 h-5 text-emerald-600" />
              Paiements reçus
              <input
                type="month"
                value={paiementsMois}
                onChange={(e) => setPaiementsMois(e.target.value)}
                className="ml-2 text-sm font-normal border border-slate-300 rounded px-2 py-1"
                data-testid="paiements-mois-input"
              />
              {paiementsMois && (
                <button
                  type="button"
                  onClick={() => setPaiementsMois("")}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                >
                  tous mois
                </button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded-lg border-2 border-emerald-200 p-3">
                <div className="flex items-center gap-2 text-emerald-600 mb-1">
                  <span className="text-lg">💶</span>
                  <span className="text-xs font-medium">Cash</span>
                </div>
                <p className="text-2xl font-bold text-emerald-700">{paiementsSummary.total_cash.toFixed(2)}<span className="text-sm ml-0.5">€</span></p>
                <p className="text-xs text-slate-500 mt-1">{paiementsSummary.count_cash} vente(s)</p>
              </div>
              <div className="bg-white rounded-lg border-2 border-blue-200 p-3">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <span className="text-lg">🏦</span>
                  <span className="text-xs font-medium">Virement total</span>
                </div>
                <p className="text-2xl font-bold text-blue-700">{paiementsSummary.total_virement.toFixed(2)}<span className="text-sm ml-0.5">€</span></p>
                <p className="text-xs text-slate-500 mt-1">{paiementsSummary.count_virement} vente(s)</p>
              </div>
              <div className="bg-white rounded-lg border border-blue-200 p-3">
                <div className="text-xs text-slate-500 mb-1">🏦 Cocoon-Elec</div>
                <p className="text-xl font-bold text-blue-700">{paiementsSummary.total_virement_cocoon.toFixed(2)}<span className="text-sm ml-0.5">€</span></p>
              </div>
              <div className="bg-white rounded-lg border border-violet-200 p-3">
                <div className="text-xs text-slate-500 mb-1">🏦 Livreur</div>
                <p className="text-xl font-bold text-violet-700">{paiementsSummary.total_virement_livreur.toFixed(2)}<span className="text-sm ml-0.5">€</span></p>
              </div>
            </div>
            {paiementsSummary.count_non_defini > 0 && (
              <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-800">
                ⚠️ {paiementsSummary.count_non_defini} vente(s) sans mode de paiement défini ({paiementsSummary.total_non_defini.toFixed(2)} €) — clique sur le badge dans la page Ventes pour les compléter.
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Rapport Journalier */}
      {rapport && (
        <div className="relative" style={{ order: cardsOrder.indexOf("rapport") }} data-testid="card-rapport-wrapper">
        <CardReorderControls id="rapport" order={cardsOrder} setOrder={setCardsOrder} editMode={editMode} save={(o) => saveOrder("dashboard_cards_order", o)} />
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="rapport-journalier-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-600" />
              Rapport du jour
              <span className="ml-2 text-xs font-normal text-slate-500">
                {new Date(rapport.date + "T00:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-livraisons-livrees">
                <div className="flex items-center gap-2 text-emerald-600 mb-1">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs font-medium">Livrées</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.livraisons_livrees}</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-ca-today">
                <div className="flex items-center gap-2 text-indigo-600 mb-1">
                  <Euro className="w-4 h-4" />
                  <span className="text-xs font-medium">CA du jour</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.ca_today.toFixed(0)}<span className="text-sm ml-0.5">€</span></p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-en-attente">
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Clock className="w-4 h-4" />
                  <span className="text-xs font-medium">En attente</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.en_attente}</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-en-cours">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <Truck className="w-4 h-4" />
                  <span className="text-xs font-medium">En cours</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.en_cours}</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-tournees-today">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <Route className="w-4 h-4" />
                  <span className="text-xs font-medium">Tournées</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.tournees_today}</p>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 p-3" data-testid="rapport-stock-alertes">
                <div className="flex items-center gap-2 text-red-600 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-medium">Stock bas</span>
                </div>
                <p className="text-2xl font-bold text-slate-800">{rapport.stock_alertes}</p>
              </div>
            </div>

            {rapport.produits_bas && rapport.produits_bas.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="rapport-produits-bas">
                <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Produits à réapprovisionner
                </p>
                <div className="flex flex-wrap gap-2">
                  {rapport.produits_bas.map((p) => (
                    <span key={p.sku} className="text-xs bg-white border border-red-200 rounded-full px-2.5 py-1 text-red-700">
                      {p.modele} {p.couleur} · <strong>{p.stock}</strong>/{p.seuil}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      )}

      {/* Alertes */}
      {stats?.alertes && stats.alertes.length > 0 && (
        <div className="relative" style={{ order: cardsOrder.indexOf("alertes") }} data-testid="card-alertes-wrapper">
        <CardReorderControls id="alertes" order={cardsOrder} setOrder={setCardsOrder} editMode={editMode} save={(o) => saveOrder("dashboard_cards_order", o)} />
        <Card className="border-amber-200 bg-amber-50" data-testid="alerts-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Alertes</p>
                <ul className="mt-1 space-y-1">
                  {stats.alertes.map((alerte, index) => (
                    <li key={index} className="text-sm text-amber-700">{alerte}</li>
                  ))}
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
        </div>
      )}

      {/* Origine des clients (sources d'acquisition) */}
      <div className="relative" style={{ order: cardsOrder.indexOf("sources") }} data-testid="card-sources-wrapper">
      <CardReorderControls id="sources" order={cardsOrder} setOrder={setCardsOrder} editMode={editMode} save={(o) => saveOrder("dashboard_cards_order", o)} />
      <Card className="border-indigo-100" data-testid="sources-acquisition-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-indigo-600" />
            Origine des clients
            <span className="text-sm font-normal text-slate-400">
              {dateDebut || dateFin ? "(période sélectionnée)" : "(tout l'historique)"}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {sources && sources.sources && sources.sources.length > 0 ? (
            <div className="space-y-3" data-testid="sources-list">
              {sources.sources.map((s) => (
                <div key={s.source} className="space-y-1" data-testid={`source-row-${s.source.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">{s.source}</span>
                    <span className="text-slate-500">
                      <span className="font-semibold text-slate-800">{s.count}</span> client{s.count > 1 ? "s" : ""}
                      {" · "}
                      <span className="font-semibold text-emerald-700">{s.ca.toFixed(0)} €</span>
                      {" · "}{s.pct}%
                    </span>
                  </div>
                  <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${s.pct}%` }} />
                  </div>
                </div>
              ))}
              <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-600">Total livré</span>
                <span className="text-slate-700">
                  <span className="font-bold">{sources.total}</span> clients · <span className="font-bold text-emerald-700">{sources.total_ca.toFixed(0)} €</span>
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-400 py-4 text-center" data-testid="sources-empty">
              Aucune donnée d'origine pour le moment. Sélectionnez la source au moment de la livraison (Mode Livreur).
            </p>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Dissociation CA Chaises / Livraisons / Remises */}
      {breakdown && (
        <div className="relative" style={{ order: cardsOrder.indexOf("ca-detail") }} data-testid="card-ca-detail-wrapper">
        <CardReorderControls id="ca-detail" order={cardsOrder} setOrder={setCardsOrder} editMode={editMode} save={(o) => saveOrder("dashboard_cards_order", o)} />
        <Card className="border-emerald-200" data-testid="breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Euro className="w-5 h-5 text-emerald-600" />
              CA détaillé — Chaises / Livraisons / Remises
              <span className="text-sm font-normal text-slate-400">
                {dateDebut || dateFin ? "(période)" : "(tout l'historique)"}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200" data-testid="breakdown-chaises">
                <div className="text-xs uppercase font-medium text-emerald-700 mb-1">🪑 CA Chaises</div>
                <div className="text-2xl font-bold text-emerald-800">{breakdown.ca_chaises.toFixed(0)} €</div>
                <div className="text-xs text-emerald-600 mt-1">
                  {breakdown.ca_total > 0 ? Math.round((breakdown.ca_chaises / breakdown.ca_total) * 100) : 0}% du CA total
                </div>
              </div>
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200" data-testid="breakdown-livraisons">
                <div className="text-xs uppercase font-medium text-blue-700 mb-1">🚚 CA Livraisons</div>
                <div className="text-2xl font-bold text-blue-800">{breakdown.ca_livraisons.toFixed(0)} €</div>
                <div className="text-xs text-blue-600 mt-1">
                  {breakdown.ca_total > 0 ? Math.round((breakdown.ca_livraisons / breakdown.ca_total) * 100) : 0}% du CA total
                </div>
              </div>
              <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 cursor-pointer hover:bg-rose-100" onClick={() => setBreakdownOpen(!breakdownOpen)} data-testid="breakdown-remises">
                <div className="text-xs uppercase font-medium text-rose-700 mb-1">💸 Remises accordées</div>
                <div className="text-2xl font-bold text-rose-800">-{breakdown.remise_totale.toFixed(0)} €</div>
                <div className="text-xs text-rose-600 mt-1">
                  🪑 -{breakdown.remise_chaises.toFixed(0)}€ · 🚚 -{breakdown.remise_livraisons.toFixed(0)}€ · <span className="underline">détail</span>
                </div>
              </div>
            </div>
            {breakdownOpen && breakdown.detail_remises && breakdown.detail_remises.length > 0 && (
              <div className="mt-4 border rounded-lg overflow-hidden" data-testid="breakdown-detail">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-left">
                    <tr>
                      <th className="p-2">Date</th><th className="p-2">Client</th><th className="p-2">Article</th>
                      <th className="p-2 text-right">Catalogue</th><th className="p-2 text-right">Vendu</th><th className="p-2 text-right">Remise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {breakdown.detail_remises.map((r, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-2 text-slate-500">{r.date}</td>
                        <td className="p-2">{r.client_nom}</td>
                        <td className="p-2 text-slate-600">{r.type}</td>
                        <td className="p-2 text-right text-slate-400 line-through">{r.prix_catalogue?.toFixed(2)} €</td>
                        <td className="p-2 text-right font-medium">{r.prix_vendu?.toFixed(2)} €</td>
                        <td className="p-2 text-right font-bold text-rose-700">-{r.remise?.toFixed(2)} €</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {breakdownOpen && (!breakdown.detail_remises || breakdown.detail_remises.length === 0) && (
              <p className="text-sm text-slate-400 py-3 text-center mt-2">Aucune remise sur la période 🎉</p>
            )}
          </CardContent>
        </Card>
        </div>
      )}
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
        <KPICard
          title="CA HT Total"
          value={stats?.total_ca_ht || 0}
          icon={Euro}
          color="primary"
        />
        <KPICard
          title="CA TTC Total"
          value={stats?.total_ca_ttc || 0}
          icon={DollarSign}
          color="info"
        />
        <KPICard
          title="Marge HT"
          value={stats?.total_marge_ht || 0}
          icon={TrendingUp}
          color="success"
        />
        <KPICard
          title="Trésorerie"
          value={stats?.cash_fin || 0}
          icon={Wallet}
          color={stats?.cash_fin >= 0 ? "success" : "danger"}
        />
      </div>

      {/* Second row KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 stagger-children">
        <KPICard
          title="Valeur Stock"
          value={stats?.valeur_stock || 0}
          icon={Package}
          color="warning"
        />
        <KPICard
          title="Produits Vendus"
          value={stats?.vendu_chaises || 0}
          icon={ShoppingCart}
          color="primary"
          suffix=""
        />
        <KPICard
          title="Marge Moyenne"
          value={stats?.marge_moyenne || 0}
          icon={BarChart3}
          color="success"
          suffix="€/unité"
        />
        <KPICard
          title="Charges TTC"
          value={stats?.total_charges_ttc || 0}
          icon={TrendingDown}
          color="danger"
        />
        <KPICard
          title="Pertes"
          value={stats?.total_pertes || 0}
          icon={PackageX}
          color="danger"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Line Chart - CA & Marge */}
        <Card className="lg:col-span-2" data-testid="revenue-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              CA HT & Trésorerie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats?.monthly_data || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="mois" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    formatter={(value) => [`${value.toFixed(2)} €`]}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ca_ht"
                    stroke="#4F46E5"
                    strokeWidth={2}
                    dot={{ fill: '#4F46E5' }}
                    name="CA HT"
                  />
                  <Line
                    type="monotone"
                    dataKey="marge_ht"
                    stroke="#10B981"
                    strokeWidth={2}
                    dot={{ fill: '#10B981' }}
                    name="Marge HT"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie Chart - Ventes par canal */}
        <Card data-testid="sales-channel-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              Répartition par Canal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 - Ventes par Modèle */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart - Ventes par Modèle */}
        <Card data-testid="sales-model-chart">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">
              Ventes par Modèle
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {modeleData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={modeleData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#64748B" fontSize={12} />
                    <YAxis type="category" dataKey="modele" stroke="#64748B" fontSize={12} width={100} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                      formatter={(value) => [`${value} unités`]}
                    />
                    <Bar dataKey="quantite" fill="#4F46E5" radius={[0, 4, 4, 0]} name="Quantité" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-slate-400">
                  Aucune vente
                </div>
              )}
            </div>
            {modeleData.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {modeleData.slice(0, 4).map((m, i) => (
                  <div key={m.modele} className="p-2 bg-slate-50 rounded-lg text-center">
                    <p className="text-xs text-slate-500 truncate">{m.modele}</p>
                    <p className="text-lg font-bold text-slate-700">{m.quantite}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table - Ventes par Couleur */}
        <Card className="lg:col-span-2" data-testid="sales-color-table">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Palette className="w-5 h-5 text-indigo-600" />
              Ventes par Couleur (Top 10)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.ventes_par_couleur && stats.ventes_par_couleur.length > 0 ? (
              <div className="overflow-x-auto max-h-80">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Modèle</TableHead>
                      <TableHead>Couleur</TableHead>
                      <TableHead className="text-right">Quantité</TableHead>
                      <TableHead className="text-right">CA HT</TableHead>
                      <TableHead className="text-right">Marge HT</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.ventes_par_couleur.slice(0, 10).map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                            {item.modele}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{item.couleur}</TableCell>
                        <TableCell className="text-right font-semibold">{item.quantite}</TableCell>
                        <TableCell className="text-right">{item.ca_ht.toFixed(2)} €</TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">
                          {item.marge_ht.toFixed(2)} €
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Palette className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Aucune vente enregistrée</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bar Chart - Ventes mensuelles */}
      <Card data-testid="monthly-sales-chart">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">
            Évolution des Ventes (unités)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats?.monthly_data || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="mois" stroke="#64748B" fontSize={12} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                  }}
                />
                <Bar dataKey="vendu" fill="#4F46E5" radius={[4, 4, 0, 0]} name="Chaises vendues" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Export buttons */}
      <Card data-testid="export-section">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">
            Exports & Rapports
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => handleExport('ventes')} data-testid="export-ventes-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter Ventes
            </Button>
            <Button variant="outline" onClick={() => handleExport('stock')} data-testid="export-stock-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter Stock
            </Button>
            <Button variant="outline" onClick={() => handleExport('dashboard')} data-testid="export-resume-btn">
              <Download className="w-4 h-4 mr-2" />
              Exporter Résumé
            </Button>
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleRapportMensuel}
              data-testid="rapport-mensuel-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Rapport mensuel PDF
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
